package com.evoapps.evoshift.domain.usecase.marketplace

import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.NotifyRelevantEmployeesUseCase
import com.evoapps.evoshift.domain.util.AppClock.now

class ApproveSwapRequestUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val notifyRelevantEmployeesUseCase: NotifyRelevantEmployeesUseCase,
    /** Reprices the shift for its new assignee (financial managers; best effort, see SH-12). */
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    suspend operator fun invoke(shiftId: String): Result<Unit> {
        return try {
            val shift = shiftRepository.getShift(shiftId).getOrThrow()
            val request = shift.swapRequest ?: throw IllegalStateException("No request")

            // Obsługa dwóch przypadków akceptacji Managera:

            // 1. Akceptacja KANDYDATA (Finał dla B i D)
            if (request.status == SwapStatus.TAKEN_PENDING) {
                val newUserId =
                    request.candidateUserId ?: throw IllegalStateException("No candidate")

                val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
                val nowStr = now().toString()

                // Finalize the swap: assign the user and clear the request
                shiftRepository.assignUser(shiftId, newUserId, null).getOrThrow()
                shiftRepository.updateSwapRequest(shiftId, null).getOrThrow()
                calculateShiftCost?.invoke(shiftId)

                // Notify the person who gave the shift away (an open shift had nobody).
                if (shift.userId != null) {
                    userRepository.sendNotification(
                        Notification(
                            userId = request.initiatorUserId,
                            title = "Shift Swap Approved",
                            message = "Your shift on ${shift.date} has been successfully taken by ${request.candidateName}.",
                            type = NotificationType.SHIFT_CLAIM_APPROVED,
                            createdAt = nowStr,
                            data = NotificationData.forShift(shift, workplaceId, NotificationData.NAME to request.candidateName, NotificationData.SUBJECT_USER_ID to request.candidateUserId)
                        )
                    )
                }

                // Notify Candidate
                userRepository.sendNotification(
                    Notification(
                        userId = newUserId,
                        title = "Shift Claim Approved",
                        message = "Your request to take the shift on ${shift.date} has been approved.",
                        type = NotificationType.SHIFT_CLAIM_APPROVED,
                        createdAt = nowStr,
                        data = NotificationData.forShift(shift, workplaceId)
                    )
                )
            }
            // 2. Akceptacja WEJŚCIA NA GIEŁDĘ (Start dla C i D)
            else if (request.status == SwapStatus.REQUESTED_BY_OWNER) {
                // Zmieniamy status na widoczny dla wszystkich
                val publicRequest =
                    request.copy(status = SwapStatus.AVAILABLE)
                shiftRepository.updateSwapRequest(shiftId, publicRequest).getOrThrow()

                // 3. Notify relevant employees
                val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
                notifyRelevantEmployeesUseCase(
                    workplaceId = workplaceId,
                    shift = shift,
                    title = "New Shift Available",
                    message = "A new shift is available on the marketplace.",
                    notificationType = NotificationType.SHIFT_SWAP_REQUEST
                )

                // Notify Initiator that it's now public
                val nowStr = now().toString()
                userRepository.sendNotification(
                    Notification(
                        userId = request.initiatorUserId,
                        title = "Shift Marketplace Update",
                        message = "Your shift on ${shift.date} is now visible on the marketplace.",
                        type = NotificationType.GENERAL,
                        createdAt = nowStr,
                        data = NotificationData.forShift(shift, workplaceId, NotificationData.KIND to NotificationData.Kind.NOW_PUBLIC)
                    )
                )
            }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
